import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Eye, Youtube, Video, GripVertical, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function MediaKitVideosTab() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    video_url: "",
    thumbnail_url: "",
    platform: "youtube",
    views: 0,
    order: 0,
    active: true
  });

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const res = await axios.get(`${API}/media-kit/featured-videos/all`);
      setVideos(res.data.videos || []);
    } catch (error) {
      toast.error("Error al cargar videos");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("admin_token");

    try {
      if (editingVideo) {
        await axios.put(
          `${API}/media-kit/featured-videos/${editingVideo.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Video actualizado");
      } else {
        await axios.post(
          `${API}/media-kit/featured-videos`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Video creado");
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchVideos();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("¿Eliminar este video?")) return;
    
    const token = localStorage.getItem("admin_token");
    try {
      await axios.delete(`${API}/media-kit/featured-videos/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Video eliminado");
      fetchVideos();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const openEdit = (video) => {
    setEditingVideo(video);
    setFormData({
      title: video.title,
      description: video.description || "",
      video_url: video.video_url,
      thumbnail_url: video.thumbnail_url || "",
      platform: video.platform || "youtube",
      views: video.views || 0,
      order: video.order || 0,
      active: video.active !== false
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingVideo(null);
    setFormData({
      title: "",
      description: "",
      video_url: "",
      thumbnail_url: "",
      platform: "youtube",
      views: 0,
      order: 0,
      active: true
    });
  };

  const formatViews = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
    return num?.toString() || "0";
  };

  // Extract YouTube thumbnail from URL
  const extractYouTubeThumbnail = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    if (match && match[2].length === 11) {
      return `https://img.youtube.com/vi/${match[2]}/maxresdefault.jpg`;
    }
    return "";
  };

  const handleUrlChange = (url) => {
    setFormData({ ...formData, video_url: url });
    
    // Auto-detect platform and thumbnail
    if (url.includes("youtube.com") || url.includes("youtu.be")) {
      setFormData(prev => ({
        ...prev,
        video_url: url,
        platform: "youtube",
        thumbnail_url: prev.thumbnail_url || extractYouTubeThumbnail(url)
      }));
    } else if (url.includes("vimeo.com")) {
      setFormData(prev => ({
        ...prev,
        video_url: url,
        platform: "vimeo"
      }));
    }
  };

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Videos Destacados</h2>
          <p className="text-sm text-gray-500">
            Videos que aparecerán en el Media Kit General
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setIsDialogOpen(true);
          }}
          className="bg-[#E91E8C] hover:bg-[#E91E8C]/90"
          data-testid="add-video-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Agregar Video
        </Button>
      </div>

      {/* Videos List */}
      {videos.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Video className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">No hay videos destacados</p>
          <p className="text-sm text-gray-400">Agrega videos de YouTube o Vimeo para mostrar en el Media Kit</p>
        </div>
      ) : (
        <div className="space-y-3">
          {videos.map((video, index) => (
            <div
              key={video.id}
              className={`flex items-center gap-4 p-4 bg-white border rounded-lg hover:shadow-md transition-shadow ${
                !video.active ? "opacity-50" : ""
              }`}
              data-testid={`video-item-${index}`}
            >
              {/* Thumbnail */}
              <div className="w-32 h-20 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                {video.thumbnail_url ? (
                  <img
                    src={video.thumbnail_url}
                    alt={video.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Video className="w-8 h-8 text-gray-400" />
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-medium text-gray-900 truncate">{video.title}</h3>
                  {video.platform === "youtube" && (
                    <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs rounded-full">
                      YouTube
                    </span>
                  )}
                  {video.platform === "vimeo" && (
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-600 text-xs rounded-full">
                      Vimeo
                    </span>
                  )}
                </div>
                {video.description && (
                  <p className="text-sm text-gray-500 truncate">{video.description}</p>
                )}
                <div className="flex items-center gap-4 mt-2 text-sm text-gray-400">
                  <span className="flex items-center gap-1">
                    <Eye className="w-4 h-4" />
                    {formatViews(video.views)} vistas
                  </span>
                  <span>Orden: {video.order}</span>
                  <a
                    href={video.video_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-[#E91E8C] hover:underline"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Ver video
                  </a>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => openEdit(video)}
                  data-testid={`edit-video-${index}`}
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(video.id)}
                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                  data-testid={`delete-video-${index}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingVideo ? "Editar Video" : "Agregar Video"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="video_url">URL del Video *</Label>
              <Input
                id="video_url"
                value={formData.video_url}
                onChange={(e) => handleUrlChange(e.target.value)}
                placeholder="https://youtube.com/watch?v=... o https://vimeo.com/..."
                required
                data-testid="input-video-url"
              />
              <p className="text-xs text-gray-500 mt-1">
                Soporta YouTube y Vimeo. El thumbnail se detecta automáticamente para YouTube.
              </p>
            </div>

            <div>
              <Label htmlFor="title">Título *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Ej: Capítulo Piloto"
                required
                data-testid="input-title"
              />
            </div>

            <div>
              <Label htmlFor="description">Descripción</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Breve descripción del video..."
                rows={2}
                data-testid="input-description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="platform">Plataforma</Label>
                <Select
                  value={formData.platform}
                  onValueChange={(value) => setFormData({ ...formData, platform: value })}
                >
                  <SelectTrigger data-testid="select-platform">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="youtube">YouTube</SelectItem>
                    <SelectItem value="vimeo">Vimeo</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="views">Vistas (manual)</Label>
                <Input
                  id="views"
                  type="number"
                  value={formData.views}
                  onChange={(e) => setFormData({ ...formData, views: parseInt(e.target.value) || 0 })}
                  placeholder="0"
                  data-testid="input-views"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="thumbnail_url">URL del Thumbnail</Label>
              <Input
                id="thumbnail_url"
                value={formData.thumbnail_url}
                onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
                placeholder="https://..."
                data-testid="input-thumbnail"
              />
              {formData.thumbnail_url && (
                <img
                  src={formData.thumbnail_url}
                  alt="Preview"
                  className="mt-2 w-full h-32 object-cover rounded"
                />
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="order">Orden</Label>
                <Input
                  id="order"
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                  data-testid="input-order"
                />
              </div>

              <div className="flex items-center gap-3 pt-6">
                <Switch
                  id="active"
                  checked={formData.active}
                  onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                  data-testid="switch-active"
                />
                <Label htmlFor="active">Activo</Label>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="bg-[#E91E8C] hover:bg-[#E91E8C]/90"
                data-testid="save-video-btn"
              >
                {editingVideo ? "Guardar Cambios" : "Agregar Video"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default MediaKitVideosTab;
